import {
  DynamicReturnPricing,
  DynamicReturnTypes,
  PricingRuleSet,
} from "@redotech/redo-model/team";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import { ShopifyCartItem } from "@redotech/shopify-client/ajax";
import { getInternationalPrice } from "./country-check";
import { ProductType } from "./product-types";

export function getCalculatedCoveragePrice({
  basePrice,
  autocheck,
  onTapcart,
  cartValueReturnableItems,
  cartValuePackageProtectionItems,
  finalSaleReturnsCartValue,
  cartItems,
  productType,
  numValidReturnProducts,
  country,
  cartHasSubscriptionItem,
}: {
  basePrice: number;
  autocheck: WidgetAutocheck;
  onTapcart: boolean;
  cartValueReturnableItems: number;
  cartValuePackageProtectionItems: number;
  finalSaleReturnsCartValue: number;
  cartItems: ShopifyCartItem[] | undefined;
  productType: string | undefined;
  numValidReturnProducts: number;
  country?: string | null;
  cartHasSubscriptionItem: boolean;
}) {
  let returnCost = 0;
  const baseReturnPrice = onTapcart ? basePrice : basePrice / 100;

  const returnCartValue = onTapcart
    ? cartValueReturnableItems
    : cartValueReturnableItems / 100;

  const fsrCartValue = onTapcart
    ? finalSaleReturnsCartValue
    : finalSaleReturnsCartValue / 100;

  if (autocheck.dynamicReturnsPricing && cartItems) {
    returnCost = calculateDynamicReturnsPrice(
      autocheck.dynamicReturnsPricing,
      baseReturnPrice,
      numValidReturnProducts,
      returnCartValue,
    );
    returnCost = onTapcart ? returnCost : Math.floor(returnCost * 100);
  } else {
    returnCost = autocheck.pricePerOrder
      ? onTapcart
        ? parseFloat(autocheck.pricePerOrder)
        : Math.floor(parseFloat(autocheck.pricePerOrder) * 100)
      : basePrice;
  }

  if (country && autocheck.internationalReturnsPricing) {
    returnCost = getInternationalPrice(
      returnCost,
      country,
      autocheck.countries,
      autocheck.internationalReturnsPricing,
    );
  }

  if (autocheck.finalSaleReturns.coverageEnabled) {
    let finalSaleReturnsCost = getFinalSaleReturnsPrice({
      cartTotal: fsrCartValue,
      pricingRuleSets: autocheck.finalSaleReturns.pricingRuleSets,
      shippingLabelCoveragePrice:
        autocheck.finalSaleReturns.shippingLabelCoveragePrice,
      country,
      minPrice: autocheck.finalSaleReturns.minPrice,
      maxPrice: autocheck.finalSaleReturns.maxPrice,
      backupPercentage: autocheck.finalSaleReturns.percentage,
    });
    finalSaleReturnsCost = onTapcart
      ? finalSaleReturnsCost
      : finalSaleReturnsCost * 100;

    // Since final sale returns price bakes in the price for returns, we can simply take
    // the max of the two prices to determine how much to charge for returns on mixed carts.
    // Final sale returns does not support dynamic pricing
    returnCost = Math.max(returnCost, finalSaleReturnsCost);
  }

  let calculatedCoveragePrice = 0;
  const packageProtectionCartValue = onTapcart
    ? cartValuePackageProtectionItems
    : cartValuePackageProtectionItems / 100;

  let packageProtectionCost = getPackageProtectionPrice({
    cartTotal: packageProtectionCartValue,
    pricingRuleSets: autocheck.packageProtection.pricingRuleSets,
    country,
    minPrice: autocheck.packageProtection.minPrice,
    maxPrice: autocheck.packageProtection.maxPrice,
    backupPercentage: autocheck.packageProtection.percentage,
    fixedSubscriptionPrice: autocheck.packageProtection.fixedSubscriptionPrice,
    cartHasSubscriptionItem,
  });
  packageProtectionCost = onTapcart
    ? packageProtectionCost
    : packageProtectionCost * 100;

  switch (productType) {
    case ProductType.RETURN_TYPE: {
      calculatedCoveragePrice = returnCost;

      break;
    }
    case ProductType.BOTH_TYPE: {
      calculatedCoveragePrice = packageProtectionCost + returnCost;

      break;
    }
    case ProductType.PACKAGE_PROTECTION_TYPE: {
      calculatedCoveragePrice = packageProtectionCost;

      break;
    }
    // No default
  }

  return calculatedCoveragePrice;
}

function getCostOfItemCoverage({
  cartTotal,
  pricingRuleSets,
  country,
  maxPrice,
  minPrice,
  backupPercentage,
}: {
  cartTotal: number;
  pricingRuleSets: PricingRuleSet[] | undefined;
  country: string | null;
  maxPrice: number | undefined;
  minPrice: number | undefined;
  backupPercentage: number;
}) {
  if (!pricingRuleSets?.length) {
    const cost = cartTotal * backupPercentage;
    return clamp(cost, minPrice ?? 0, maxPrice ?? Infinity);
  }

  const ruleset = determineRuleSet(pricingRuleSets, country);
  if (ruleset) {
    const rule = getApplicableRule(ruleset, cartTotal);
    const cost =
      ruleset.type === "percentage" ? rule.value * cartTotal : rule.value;
    return clamp(cost, minPrice ?? 0, maxPrice ?? Infinity);
  }

  const cost = cartTotal * backupPercentage;
  return clamp(cost, minPrice ?? 0, maxPrice ?? Infinity);
}

export function getFinalSaleReturnsPrice({
  cartTotal,
  pricingRuleSets,
  shippingLabelCoveragePrice,
  country,
  minPrice,
  maxPrice,
  backupPercentage,
}: {
  cartTotal: number;
  pricingRuleSets: PricingRuleSet[] | undefined;
  shippingLabelCoveragePrice: number | undefined | null;
  country: string | undefined | null;
  minPrice: number | undefined;
  maxPrice: number | undefined;
  backupPercentage: number;
}) {
  if (
    cartTotal === 0 ||
    country === undefined ||
    (!backupPercentage && !pricingRuleSets?.length)
  ) {
    return 0;
  }

  const costOfItemCoverage = getCostOfItemCoverage({
    cartTotal,
    pricingRuleSets,
    country,
    maxPrice,
    minPrice,
    backupPercentage,
  });
  const costOfShippingLabelCoverage = shippingLabelCoveragePrice ?? 0;
  const totalCoveragePrice = costOfItemCoverage + costOfShippingLabelCoverage;
  return parseFloat(totalCoveragePrice.toFixed(2));
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function getPackageProtectionPrice({
  cartTotal,
  pricingRuleSets,
  country,
  minPrice,
  maxPrice,
  backupPercentage,
  fixedSubscriptionPrice,
  cartHasSubscriptionItem,
}: {
  cartTotal: number;
  pricingRuleSets: PricingRuleSet[] | undefined;
  country: string | undefined | null;
  minPrice: number;
  maxPrice: number;
  backupPercentage: number;
  fixedSubscriptionPrice?: number;
  cartHasSubscriptionItem?: boolean;
}) {
  if (
    country === undefined ||
    !maxPrice ||
    (!backupPercentage && !pricingRuleSets?.length)
  ) {
    return 0;
  }

  if (cartHasSubscriptionItem && fixedSubscriptionPrice) {
    return fixedSubscriptionPrice;
  }

  if (!pricingRuleSets?.length) {
    return clamp(cartTotal * backupPercentage, minPrice, maxPrice);
  }

  const ruleset = determineRuleSet(pricingRuleSets, country);
  if (ruleset) {
    const rule = getApplicableRule(ruleset, cartTotal);
    const price =
      ruleset.type === "percentage" ? rule.value * cartTotal : rule.value;
    return clamp(price, minPrice, maxPrice);
  }

  return parseFloat(
    clamp(cartTotal * backupPercentage, minPrice, maxPrice).toFixed(2),
  );
}

function determineRuleSet(
  pricingRuleSets: PricingRuleSet[],
  country: string | null,
) {
  for (const priceSet of pricingRuleSets) {
    if (
      country === null ||
      priceSet.countries.includes(country) ||
      priceSet.countries.length == 0
    ) {
      return priceSet;
    }
  }
  return null;
}

function getApplicableRule(ruleSet: PricingRuleSet, cartTotal: number) {
  if (!cartTotal) {
    return ruleSet.priceBrackets[0];
  }
  for (let i = 0; i < ruleSet.priceBrackets.length; i++) {
    if (i === ruleSet.priceBrackets.length - 1) {
      return ruleSet.priceBrackets[i];
    }

    if (
      cartTotal > ruleSet.priceBrackets[i].pricePoint &&
      cartTotal <= ruleSet.priceBrackets[i + 1].pricePoint
    ) {
      return ruleSet.priceBrackets[i];
    }
  }
  return ruleSet.priceBrackets[ruleSet.priceBrackets.length - 1];
}

export function calculateDynamicReturnsPrice(
  dynamicReturns: DynamicReturnPricing,
  basePrice: number,
  numItems: number,
  cartTotal: number,
) {
  if (numItems === 0) {
    return 0;
  }

  switch (dynamicReturns.type) {
    case DynamicReturnTypes.ITEM_COUNT:
      return parseFloat(
        Math.min(
          basePrice + dynamicReturns.pricePerAdditonalItem * (numItems - 1),
          dynamicReturns.maxPrice,
        ).toFixed(2),
      );
    case DynamicReturnTypes.CART_VALUE:
      return parseFloat(
        getPackageProtectionPrice({
          cartTotal,
          pricingRuleSets: [dynamicReturns.pricingRuleSet],
          country: null,
          minPrice: basePrice,
          maxPrice: dynamicReturns.maxPrice,
          backupPercentage: 0,
        }).toFixed(2),
      );
  }
}
